import { z } from 'zod';

// ---------------------------------------------------------------------------
// Params & Queries
// ---------------------------------------------------------------------------

export const scheduleIdParam = z.object({
  id: z.string().uuid(),
});

export const listSchedulesQuery = z.object({
  hub_id: z.string().uuid(),
});

export const listExecutionsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(50).default(10),
});

export const createScheduleBody = z.object({
  hub_id: z.string().uuid(),
  schedule_name: z.string().min(1),
  description: z.string().nullable().optional(),
  cron_expression: z.string().min(1),
  timezone: z.string().default('UTC'),
  list_id: z.string().uuid(),
  channel_type: z.enum(['whatsapp', 'instagram', 'email', 'app']),
  connection_id: z.string().uuid().nullable().optional(),
  execution_mode: z.enum(['direct_message', 'agent_trigger']).default('direct_message'),
  template_whatsapp_id: z.string().uuid().nullable().optional(),
  initial_message: z.string().nullable().optional(),
  agent_id: z.string().uuid().nullable().optional(),
  trigger_message: z.string().nullable().optional(),
});

export const updateScheduleBody = z.object({
  schedule_name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  cron_expression: z.string().min(1).optional(),
  timezone: z.string().optional(),
  list_id: z.string().uuid().optional(),
  channel_type: z.enum(['whatsapp', 'instagram', 'email', 'app']).optional(),
  connection_id: z.string().uuid().nullable().optional(),
  execution_mode: z.enum(['direct_message', 'agent_trigger']).optional(),
  template_whatsapp_id: z.string().uuid().nullable().optional(),
  initial_message: z.string().nullable().optional(),
  agent_id: z.string().uuid().nullable().optional(),
  trigger_message: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type ScheduleIdParam = z.infer<typeof scheduleIdParam>;
export type ListSchedulesQuery = z.infer<typeof listSchedulesQuery>;
export type ListExecutionsQuery = z.infer<typeof listExecutionsQuery>;
export type CreateScheduleBody = z.infer<typeof createScheduleBody>;
export type UpdateScheduleBody = z.infer<typeof updateScheduleBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface OutboundSchedule {
  schedule_id: string;
  hub_id: string;
  schedule_name: string;
  description: string | null;
  enabled: boolean;
  cron_expression: string;
  timezone: string;
  list_id: string;
  channel_type: string;
  connection_id: string | null;
  execution_mode: string;
  template_whatsapp_id: string | null;
  initial_message: string | null;
  agent_id: string | null;
  trigger_message: string | null;
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  created_at: string;
  updated_at: string;
}

export interface ListSchedulesResponse {
  schedules: OutboundSchedule[];
}

export interface CreateScheduleResponse {
  schedule_id: string;
}

export interface ScheduleExecution {
  execution_id: string;
  schedule_id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  total_contacts: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export interface ListExecutionsResponse {
  executions: ScheduleExecution[];
  total: number;
  page: number;
  per_page: number;
}
